import pubsub from '@/lib/pubsub'
import { getOfflineMapService } from '@/services/offline-map-service/offline-map-service-instance'
import { getUpdateInfoForTilesets } from '@/services/offline-map-service/get-download-size-estimate'
import type {
  GetZoomLevelsForEachTilesetByMapDetailLevelResponse,
  OfflineMapServiceState,
  TransformedRegionToGeoJson,
  UnitType,
} from '@/services/offline-map-service/types'
import {
  getDistancesFromPoints,
  updateOfflineStateMapArea,
} from '@/services/offline-map-service/helpers'
import type { OFFLINE_MAPS_ORIGINS } from '@/lib/enums-marketing'
import { getItem } from '@/plugins/storage'
import { captureException } from '@sentry/vue'

/**
 * Transforms data from map & offlineServiceState and publishes 'offline_map_deleted' marketing event
 * @param map
 * @param origin
 * @param offlineServiceState
 * @returns Promise<void>
 */
export const deleteMapEvent = async (
  map: TransformedRegionToGeoJson,
  origin:
    | typeof OFFLINE_MAPS_ORIGINS.MAP_EDIT_CARD
    | typeof OFFLINE_MAPS_ORIGINS.MAP_INFO_CARD
    | typeof OFFLINE_MAPS_ORIGINS.MENU,
  offlineServiceState: OfflineMapServiceState
) => {
  try {
    // Set estimateInMB
    let updateInfo
    let estimate
    let estimateInMb
    const zoomLevelsData =
      await getOfflineMapService().getZoomLevelsForDetailLevel(map.map_detail)
    const { tilesets } = zoomLevelsData ?? {}
    if (tilesets) {
      updateInfo = await getUpdateInfoForTilesets(
        tilesets as unknown as GetZoomLevelsForEachTilesetByMapDetailLevelResponse['tilesets'],
        map.geometry.coordinates[0]
      )
    }
    if (updateInfo) {
      estimate = updateInfo.reduce(
        (accumulator, tileset) => accumulator + (tileset?.size_in_bytes ?? 0),
        0
      )
    }
    if (estimate) {
      estimateInMb = estimate / (1024 * 1024)
    }

    const unitType: UnitType = getItem('unitType')

    const { widthDistance, heightDistance } = getDistancesFromPoints(
      map.geometry.coordinates[0][2],
      map.geometry.coordinates[0][3],
      map.geometry.coordinates[0][4],
      unitType
    )

    // Set map_area
    updateOfflineStateMapArea(widthDistance, heightDistance, unitType)

    pubsub.publish('x-marketing-event', {
      name: 'offline_map_deleted',
      map_storage: estimateInMb,
      // map area in sq kilometers
      map_area: offlineServiceState.map_area,
      origin: origin,
      // UUID for the map that was deleted.
      map_uuid: map.uuid,
    })
  } catch (error) {
    captureException(error, {
      tags: { feature: 'offline-map-service', action: 'delete_map_event' },
    })
  }
}
